import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Bbox } from 'ais-facade-api/api';
import { RootState } from 'app/store/store';
import { BASE_URL } from 'app/utils/utils';
import i18next from 'i18next';

const weatherLayers = JSON.parse(window._env_?.WEATHER_LAYERS || null);

export const weatherApi = createApi({
  reducerPath: 'weather',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/weather',
    credentials: 'include',
    mode: 'cors',
    prepareHeaders: (headers: Headers, { getState }) => {
      const userInfo = (getState() as RootState).userSlice;
      headers.set('Accept-Language', i18next.resolvedLanguage ? i18next.resolvedLanguage : 'ru');

      if (userInfo?.auth && userInfo?.user) {
        headers.set('x-ais-user-csrf', userInfo.auth.value.csrfToken);
        headers.set('ais-user-csrf', userInfo.auth.value.csrfToken);
      }
      return headers;
    },
  }),
  tagTypes: ['Weather'],

  endpoints: (build) => ({
    getWindBarbs: build.query<any, { bbox: Bbox; width: number; height: number }>({
      query: ({ bbox, width, height }) => ({
        url: `/`,
        responseHandler: (res) => {
          return res.blob();
        },

        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        },
        params: {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TRANSPARENT: true,
          tiled: true,
          STYLES: 'WIND_BARB',
          LAYERS: `${weatherLayers?.wind}`,
          exceptions: 'application/vnd.ogc.se_xml',
          WIDTH: width,
          HEIGHT: height,
          CRS: 'EPSG:4326',
          BBOX: `${bbox?.first.latitude},${bbox?.first.longitude},${bbox?.second.latitude},${bbox?.second?.longitude}`,
        },
      }),
    }),

    getWindImage: build.query<any, void>({
      query: () => ({
        url: `/`,
        responseHandler: (res) => {
          return res.blob();
        },
        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        },
        params: {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TRANSPARENT: true,
          tiled: true,
          LAYERS: `${weatherLayers?.wind}`,
          exceptions: 'application/vnd.ogc.se_xml',
          WIDTH: 1440,
          HEIGHT: 721,
          CRS: 'EPSG:4326',
          BBOX: `-90.125,-0.125,90.125,359.875`,
        },
      }),
    }),

    getTemp: build.query<Blob, { bbox: Bbox; width?: number; height?: number }>({
      query: ({ bbox, width, height }) => ({
        url: `/`,
        responseHandler: (res) => {
          return res.blob();
        },

        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        },
        params: {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TRANSPARENT: true,
          // LAYERS: `DEV_RESULT_TEMPERATURE`,
          LAYERS: `${weatherLayers?.temperature}`,
          exceptions: 'application/vnd.ogc.se_xml',
          WIDTH: width,
          HEIGHT: height,
          CRS: 'EPSG:3857',
          BBOX: `${bbox?.first.longitude},${bbox?.first.latitude},${bbox?.second.longitude},${bbox?.second?.latitude}`,
        },
      }),
    }),
    getWaves: build.query<Blob, { bbox: Bbox; width?: number; height?: number }>({
      query: ({ bbox, width, height }) => ({
        url: `/`,
        responseHandler: (res) => {
          return res.blob();
        },

        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        },
        params: {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TRANSPARENT: true,
          // LAYERS: `DEV_WAVES`,
          LAYERS: `${weatherLayers?.waves}`,
          exceptions: 'application/vnd.ogc.se_xml',
          WIDTH: width,
          HEIGHT: height,
          CRS: 'EPSG:3857',
          BBOX: `${bbox?.first.longitude},${bbox?.first.latitude},${bbox?.second.longitude},${bbox?.second?.latitude}`,
        },
      }),
    }),
    getPressure: build.query<Blob, { bbox: Bbox; width?: number; height?: number }>({
      query: ({ bbox, width, height }) => ({
        url: `/`,
        responseHandler: (res) => {
          return res.blob();
        },

        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        },
        params: {
          SERVICE: 'WMS',
          VERSION: '1.3.0',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TRANSPARENT: true,
          LAYERS: `${weatherLayers?.pressure}`,
          exceptions: 'application/vnd.ogc.se_xml',
          WIDTH: width,
          HEIGHT: height,
          CRS: 'EPSG:3857',
          BBOX: `${bbox?.first.longitude},${bbox?.first.latitude},${bbox?.second.longitude},${bbox?.second?.latitude}`,
        },
      }),
    }),
    getFeatureInfo: build.query<any, { bbox: Bbox; layers: string }>({
      query: ({ bbox, layers }) => ({
        url: `/`,
        method: 'GET',
        params: {
          SERVICE: 'WMS',
          VERSION: '1.1.1',
          REQUEST: 'GetFeatureInfo',
          FORMAT: 'application/json',
          QUERY_LAYERS: layers,
          LAYERS: layers,
          exceptions: 'application/vnd.ogc.se_inimage',
          INFO_FORMAT: 'application/json',
          FEATURE_COUNT: 50,
          X: 50,
          Y: 50,
          SRS: 'EPSG:4326',
          WIDTH: 101,
          HEIGHT: 101,
          BBOX: `${bbox.first.latitude},${bbox.first.longitude},${bbox.second.latitude},${bbox.second.longitude}`,
          feature_count: 100,
        },
      }),
    }),
  }),
});
export const {
  useLazyGetFeatureInfoQuery,
  useLazyGetPressureQuery,
  useLazyGetTempQuery,
  useLazyGetWavesQuery,
  useLazyGetWindBarbsQuery,
  useLazyGetWindImageQuery,
} = weatherApi;
